import { applyEdgeChanges, applyNodeChanges, type EdgeChange, type NodeChange } from '@xyflow/react';
import type { CanvasState, SliceCreator } from '../types';
import { pushHistory } from '../utils';
import type { SliceCtx } from '../ctx';

type SliceKeys = 'loadGraph' | 'replaceGraph' | 'applyNodesChange' | 'applyEdgesChange';

export const createGraphSlice: SliceCreator<Pick<CanvasState, SliceKeys>> = (set, get) => {
  const resolveCtx = (): SliceCtx | undefined => (get as any).__ctx;

  const ensureCtx = () => {
    const ctx = resolveCtx();
    if (!ctx) throw new Error('canvas ctx missing for graph slice');
    return ctx;
  };

  return {
    loadGraph: ({ id, graph, revision, name }) => {
      const { queueInference, bumpStructureVersion, debugLog } = ensureCtx();
      debugLog?.('loadGraph', { id, revision, name });
      set((state) => {
        state.graph.id = id;
        state.graph.nodes = graph.nodes ?? [];
        state.graph.edges = graph.edges ?? [];
        state.graph.meta.revision = revision;
        state.graph.meta.name = name;
        state.ui.history = { past: [], future: [] };
        state.ui.selection = { nodes: new Set(), edges: new Set() };
        bumpStructureVersion(state);
      });
      queueInference({ immediate: true });
    },

    replaceGraph: (graph, meta) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        state.graph.nodes = graph.nodes ?? [];
        state.graph.edges = graph.edges ?? [];
        if (meta?.id) state.graph.id = meta.id;
        if (typeof meta?.name !== 'undefined') state.graph.meta.name = meta?.name;
        if (typeof meta?.revision !== 'undefined') state.graph.meta.revision = meta?.revision;
        state.ui.history = { past: [], future: [] };
        state.ui.selection = { nodes: new Set(), edges: new Set() };
        bumpStructureVersion(state);
      });
      queueInference({ immediate: true });
    },

    applyNodesChange: (changes: NodeChange[]) => {
      const { queueInference, bumpStructureVersion, cancelQueuedInference } = ensureCtx();
      const isGeometryChange = (change: NodeChange) =>
        change.type === 'position' || change.type === 'dimensions';
      const hasNonSelect = changes.some((change) => change.type !== 'select');
      const hasDragging = changes.some(
        (change) => change.type === 'position' && !!(change as any).dragging,
      );
      const onlyGeometryOrSelect = changes.every(
        (change) => isGeometryChange(change) || change.type === 'select',
      );
      const structureChange = changes.some((change) =>
        change.type === 'remove' || change.type === 'add' || change.type === 'reset',
      );

      set((state) => {
        const shouldPushHistory = hasNonSelect && !(onlyGeometryOrSelect && hasDragging);
        if (shouldPushHistory) pushHistory(state);
        const nextNodes = applyNodeChanges(changes, state.graph.nodes);
        if (nextNodes !== state.graph.nodes) {
          state.graph.nodes = nextNodes;
          if (structureChange) bumpStructureVersion(state);
        }
      });

      if (!hasNonSelect) return;
      if (onlyGeometryOrSelect && hasDragging) {
        cancelQueuedInference();
        return;
      }
      queueInference({ immediate: true });
    },

    applyEdgesChange: (changes: EdgeChange[]) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      const structureChange = changes.some(
        (change) => change.type === 'remove' || change.type === 'add' || change.type === 'reset',
      );
      const meaningful = changes.some((change) => change.type !== 'select');
      set((state) => {
        if (meaningful) pushHistory(state);
        const nextEdges = applyEdgeChanges(changes, state.graph.edges);
        if (nextEdges !== state.graph.edges) {
          state.graph.edges = nextEdges;
          if (structureChange) bumpStructureVersion(state);
        }
      });
      if (meaningful) queueInference({ immediate: true });
    },
  };
};
